// materialBinding — the rule deciding what a rig mesh wears when the character's
// material_mapping does NOT name a material for it.
//
// Pure (no THREE, no DOM): applyMaterialMapping walks the scene, this decides.
// Split out because the rule is the whole bug — the traversal never was.
//
// WHAT WENT WRONG (Vaelmor, 2026-08-31, reported by Alessandro): an unbound
// mesh used to inherit `fallback`, which buildMaterialsFromAssets set to "the
// first mapping entry that resolved a material". The mapping is ordered by
// meshIndex, so that is ALWAYS Element 0 — belt_buckle on Vaelmor. Assigning a
// material to Element 0 therefore repainted every unbound mesh on the
// character, and the workaround people found ("bind Element 0 first, then the
// rest, and it comes right") is the same fact from the other side: filling in
// every slot merely leaves the substitution with no victims. It never stopped
// happening.
//
// The fallback was deliberate — the old comment said it existed "so the avatar
// never renders with gray defaults". That trade is wrong twice over. It breaks
// the repo's no-silent-fallback rule (an unbound mesh renders looking bound, so
// the artist has no way to see what is missing), and grey defaults are the
// SIGNAL, not the failure: PlayCanvas binds materials to mesh instances
// positionally and gives an unassigned, removed or unloaded slot
// `system.defaultMaterial` — an engine-owned neutral, never a neighbour's
// material (engine/src/framework/components/render/component.js, `_cloneMeshes`
// / `_onMaterialRemove`). Slot 0 is special there too, but strictly as a
// read-only alias: `_updateMainMaterial` mirrors it into `component.material`
// when `index === 0` and never writes it onto another mesh instance.
//
// So: an unbound mesh wears one shared neutral placeholder, and WHY it is
// unbound is reported rather than hidden. The three causes need different
// answers from whoever reads the console, which is why they are not one blob:

/** Mesh has a material of its own. */
export const BOUND = 'bound'
/** Mapping marks the mesh hidden — not a binding failure. */
export const HIDDEN = 'hidden'
/** Mapping row exists and names no material. Author it in the Rig Inspector. */
export const UNASSIGNED = 'unassigned'
/** Mapping row names a materialId no material_assets row answers to — a
 *  DANGLING REFERENCE (row deleted, or a mapping carried over from another
 *  character). Data integrity, not an authoring gap: reported as an error. */
export const UNRESOLVED = 'unresolved'
/** Mesh node the mapping never mentions at all. The mapping is derived one row
 *  per rendered primitive (materialMappingFromGlb / the server's glb_import),
 *  so this means the mapping is STALE against the rig now loaded. */
export const UNMAPPED = 'unmapped'
/** Mapping row names a material that EXISTS but whose `type` is not `graph`.
 *  Every material is a node graph (ENG-814 Phase 2b removed the pbr/toon/skin
 *  render path), so such a row has nothing to render with. Kept apart from
 *  UNRESOLVED because the reference is fine and the DATA is behind: the fix is
 *  to run the migration on this character, not to hunt a deleted row. */
export const LEGACY_TYPE = 'legacyType'

/**
 * Decide which of the five states a scene mesh is in.
 *
 * @param {string} nodeName        THREE node name (what the mapping keys on).
 * @param {object} sets            Name sets from buildMaterialsFromAssets:
 *   @param {Set<string>} sets.bound       rows that resolved a material
 *   @param {Set<string>} sets.hidden      rows flagged hidden
 *   @param {Set<string>} sets.unresolved  rows whose materialId did not resolve
 *   @param {Set<string>} sets.mapped      every row, whatever its state
 * @returns {string} one of the state constants above.
 */
export function classifyMeshBinding(nodeName, sets) {
  // Hidden first: a hidden row may also be unassigned, and "don't draw it"
  // outranks "nothing is bound to it".
  if (sets.hidden?.has(nodeName)) return HIDDEN
  if (sets.bound?.has(nodeName)) return BOUND
  if (sets.unresolved?.has(nodeName)) return UNRESOLVED
  // Before UNASSIGNED: the row DOES name a material, so "nothing assigned" is
  // the wrong complaint and would send the reader to the Rig Inspector.
  if (sets.legacyType?.has(nodeName)) return LEGACY_TYPE
  if (sets.mapped?.has(nodeName)) return UNASSIGNED
  return UNMAPPED
}

/** A mesh in this state renders the neutral placeholder. */
export function isUnbound(state) {
  return state === UNASSIGNED || state === UNRESOLVED || state === UNMAPPED || state === LEGACY_TYPE
}

export function emptyBindingReport() {
  return {
    bound: [], hidden: [], unassigned: [], unresolved: [], unmapped: [], legacyType: [],
    // The character has NO material_mapping rows at all — a real state (the AAM
    // stores an uploaded rig without parsing it), and categorically different
    // from a mapping that omits a mesh. Nothing is rebound in that case; the
    // GLB's embedded materials stand. Reported so it is visible rather than
    // looking like a character whose bindings simply all worked.
    unmappedCharacter: false,
  }
}

/** Record one mesh into a report. Names may repeat — two nodes can share a
 *  name, and the count is part of what the reader needs. */
export function recordBinding(report, state, nodeName) {
  report[state]?.push(nodeName)
  return report
}

/** Stable identity for "the same complaint as last time", so a report repeated
 *  by the second texture-tier pass (or by every inspector PATCH, which calls
 *  reloadMaterialsEverywhere) is not re-logged. Sorted: traversal order is not
 *  a meaningful difference. */
export function bindingSignature(report) {
  return [
    // Load-bearing: an unmapped character's per-mesh lists are all EMPTY, so
    // without this it signs identically to a fully bound one and switching
    // between the two would log nothing.
    `nomap:${report.unmappedCharacter ? 1 : 0}`,
    ...[UNRESOLVED, LEGACY_TYPE, UNASSIGNED, UNMAPPED].map(k => `${k}:${[...report[k]].sort().join(',')}`),
  ].join('|')
}

/**
 * The console lines this report deserves, most severe first. Empty when every
 * mesh is bound — silence is then correct, because there is nothing hidden.
 * @returns {Array<{level: 'error'|'warn', text: string}>}
 */
export function bindingMessages(report) {
  const out = []
  const list = (names) => [...new Set(names)].sort().join(', ')
  if (report.unmappedCharacter) {
    return [{
      level: 'warn',
      text: '[materials] this character has no material_mapping — rendering the rig GLB\'s embedded '
        + 'materials. Derive the slot list from the Rig Inspector to bind materials to it.',
    }]
  }
  if (report.unresolved.length) {
    out.push({
      level: 'error',
      text: `[materials] ${report.unresolved.length} mesh(es) reference a material that does not exist — `
        + `the material_assets row was deleted, or this mapping belongs to another character: ${list(report.unresolved)}`,
    })
  }
  if (report.legacyType.length) {
    out.push({
      level: 'error',
      text: `[materials] ${report.legacyType.length} mesh(es) are bound to a legacy pbr/toon/skin material, `
        + `which no longer has a render path — run the migration for this character `
        + `(POST /api/characters/{slug}:migrateMaterialsToGraph): ${list(report.legacyType)}`,
    })
  }
  if (report.unmapped.length) {
    out.push({
      level: 'error',
      text: `[materials] ${report.unmapped.length} mesh(es) are absent from material_mapping — `
        + `the mapping is stale against this rig; re-derive it from the Rig Inspector: ${list(report.unmapped)}`,
    })
  }
  if (report.unassigned.length) {
    out.push({
      level: 'warn',
      text: `[materials] ${report.unassigned.length} mesh(es) have no material assigned and render the neutral `
        + `placeholder: ${list(report.unassigned)}`,
    })
  }
  return out
}
